import type { SharedData } from "@src/context/ApplicationContext";
import type { UTMParams } from "@src/context/UTMContext";
import ClientLogsService from "@src/services/client-logs.service";
import { postUserDigitalInfo } from "@src/services/user-digital-info.service";
import { buildDigitalFingerprint } from "@src/utils/digital-fingerprint";
import { useCallback, useEffect, useRef } from "react";

interface UseEntryDigitalInfoBootstrapOptions {
  enabled?: boolean;
  userUuid?: string;
  hasPostedDigitalInfo?: boolean;
  forcePostForUserUuid?: boolean;
  canvasIsInSalesforce: boolean;
  canvasIsLoading: boolean;
  canvasPrefillVersion?: SharedData["canvasPrefillVersion"];
  setData: (key: keyof SharedData, value: any) => void;
  utmTags?: UTMParams;
  logLocation: string;
}

export const useEntryDigitalInfoBootstrap = ({
  enabled = true,
  userUuid,
  hasPostedDigitalInfo,
  forcePostForUserUuid = false,
  canvasIsInSalesforce,
  canvasIsLoading,
  canvasPrefillVersion,
  setData,
  utmTags,
  logLocation,
}: UseEntryDigitalInfoBootstrapOptions) => {
  const postedDigitalInfoUserUuidsRef = useRef(new Set<string>());
  const postDigitalInfoLocation = logLocation.endsWith(".postDigitalInfo")
    ? logLocation
    : `${logLocation}.postDigitalInfo`;

  const postDigitalInfo = useCallback(
    async (currentUserUuid: string) => {
      postedDigitalInfoUserUuidsRef.current.add(currentUserUuid);

      try {
        const fingerprint = await buildDigitalFingerprint(utmTags);
        await postUserDigitalInfo(currentUserUuid, fingerprint);
        setData("hasPostedDigitalInfo", true);
      } catch (error) {
        postedDigitalInfoUserUuidsRef.current.delete(currentUserUuid);
        ClientLogsService.logError(
          currentUserUuid,
          postDigitalInfoLocation,
          "Failed to post digital info",
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    },
    [postDigitalInfoLocation, setData, utmTags],
  );

  // Non-Canvas flow: post digital info once when a userUuid exists.
  // Wait for Canvas initialization to complete, then check context state to determine flow.
  // Canvas sessions are intentionally excluded here and handled in the prefill effect below.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Wait for Canvas initialization to resolve before proceeding with non-Canvas posting.
    if (canvasIsLoading || canvasIsInSalesforce || !userUuid) {
      return;
    }

    if (
      (!forcePostForUserUuid && hasPostedDigitalInfo) ||
      postedDigitalInfoUserUuidsRef.current.has(userUuid)
    ) {
      return;
    }

    void postDigitalInfo(userUuid);
  }, [
    canvasIsLoading,
    canvasIsInSalesforce,
    userUuid,
    hasPostedDigitalInfo,
    forcePostForUserUuid,
    postDigitalInfo,
    enabled,
  ]);

  // Canvas flow: only post digital info after a Canvas prefill event is applied
  // (indicated by canvasPrefillVersion) and a userUuid is available.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!canvasIsInSalesforce || canvasIsLoading) {
      return;
    }

    if (!canvasPrefillVersion || !userUuid) {
      return;
    }

    if (
      (!forcePostForUserUuid && hasPostedDigitalInfo) ||
      postedDigitalInfoUserUuidsRef.current.has(userUuid)
    ) {
      return;
    }

    void postDigitalInfo(userUuid);
  }, [
    canvasIsInSalesforce,
    canvasIsLoading,
    canvasPrefillVersion,
    userUuid,
    hasPostedDigitalInfo,
    forcePostForUserUuid,
    postDigitalInfo,
    enabled,
  ]);
};
